import {
  X,
  Search,
  Globe,
  User,
  Settings,
  LogOut,
  CheckCircle,
  Check,
  CircleDollarSign,
  LogIn,
  UserPlus,
  Home,
  Stethoscope,
  Building,
  FileText,
} from 'lucide-react';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { CurrencySwitcher } from '@/components/currency/CurrencySwitcher.tsx';
import { Link, Button, Badge } from '@/components/design-system';
import {
  COLOR_SCALE,
  FONT_WEIGHTS,
  GAP_SCALE,
  INTERACTIVE_STATES,
  MARGIN_AUTO,
  MARGIN_BOTTOM,
  MARGIN_LEFT,
  MARGIN_TOP,
  MARGIN_X,
  OVERFLOW_Y,
  PADDING_BOTTOM,
  PADDING_TOP,
  PADDING_X,
  PADDING_Y,
  RADIUS_SCALE,
  RESPONSIVE_SIZE_SCALE,
  SHADOW_SCALE,
  SIZE_SCALE,
  SPACE_X_SCALE,
  SPACE_Y_SCALE,
  SURFACE_PADDING,
  TYPE_SCALE,
  RESPONSIVE_DISPLAY,
  POSITION_SCALE,
  FLEX,
  FLEX_ALIGN as FLEX_ALIGN,
  FLEX_JUSTIFY as FLEX_JUSTIFY,
} from '@/components/design-system/tokens';
import { languages } from '@/components/layout/constants';
import { withMemoryLeakMonitor } from '@/components/ui/MemoryLeakMonitor';
import { useCurrency } from '@/hooks/useCurrency';
import { createPageUrl } from '@/utils';

import { useLanguageNotification } from '../hooks/useLanguageNotification';

// Language Selector Component for mobile menu
const LanguageSelector = ({
  currentLanguage,
  variant: _variant,
}: {
  currentLanguage: string;
  variant: string;
}) => (
  <div className="flex items-center justify-center">
    <span className={`${FONT_WEIGHTS.medium} text-sm`}>{currentLanguage.toUpperCase()}</span>
  </div>
);

// Currency Selector Component for mobile menu
const CurrencySelector = ({
  selectedCurrency,
  variant: _variant,
}: {
  selectedCurrency: string;
  variant: string;
}) => (
  <div className="flex items-center justify-center">
    <span className={`${FONT_WEIGHTS.medium} text-sm`}>{selectedCurrency}</span>
  </div>
);

/**
 * MobileExpandableMenu - Combines user menu, language selector, and search in one expandable panel
 * Opens from the right and expands to the left on mobile devices
 */

// Helper function to get user display information
const getUserDisplayInfo = (user: MobileExpandableMenuUser | null, defaultAccountLabel: string) => {
  const rawDisplayName = user
    ? (user.full_name?.trim() ?? user.email?.trim() ?? defaultAccountLabel)
    : defaultAccountLabel;
  const uppercaseDisplayName = rawDisplayName.toUpperCase();
  const avatarInitial = rawDisplayName.charAt(0).toUpperCase();
  const hasProfileImage = Boolean(user?.profile_image_url?.trim());

  return {
    rawDisplayName,
    uppercaseDisplayName,
    avatarInitial,
    hasProfileImage,
  };
};

// Avatar component
const UserAvatar = React.memo(
  ({
    user,
    rawDisplayName,
    avatarInitial,
    hasProfileImage,
  }: {
    user: MobileExpandableMenuUser | null;
    rawDisplayName: string;
    avatarInitial: string;
    hasProfileImage: boolean;
  }) => {
    if (user) {
      return hasProfileImage ? (
        <img
          src={user.profile_image_url!}
          alt={rawDisplayName}
          className={`${SIZE_SCALE.w7} ${SIZE_SCALE['7']} rounded-full object-cover`}
          onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
            const target = e.target as HTMLImageElement;
            target.style.display = 'none';
            const nextSibling = target.nextElementSibling as HTMLElement;
            nextSibling.style.display = 'flex';
          }}
          role="presentation"
        />
      ) : (
        <div
          className={`${SIZE_SCALE.w7} ${SIZE_SCALE['7']} rounded-full bg-gradient-to-br from-gray-700 to-gray-500 flex items-center justify-center text-white text-xs font-bold`}
        >
          {avatarInitial}
        </div>
      );
    }

    return <User className={`${SIZE_SCALE.w5} ${SIZE_SCALE['5']}`} />;
  }
);

UserAvatar.displayName = 'UserAvatar';
interface MobileExpandableMenuUser {
  role?: string;
  is_admin?: boolean;
  is_marketer?: boolean;
  is_partner?: boolean | null;
  partner_category?: string | null;
  assigned_provider_ids?: string[] | null;
  full_name?: string;
  email?: string;
  profile_image_url?: string | null;
  partner_permissions?: boolean;
}

interface MobileExpandableMenuProps {
  user: MobileExpandableMenuUser | null;
  currentLanguage: string;
  changeLanguage: (language: string) => void;
  setShowSearch: (show: boolean) => void;
  handleLogout: () => void;
  handleSignup: () => void;
  handleLogin: () => void;
  headerOnLight: boolean;
  pendingReportCount: number;
  t: (
    namespace: string,
    key: string,
    fallback?: string,
    variables?: Record<string, unknown>
  ) => string;
}

const useMobileMenu = () => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const closeMenu = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        closeMenu();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, closeMenu]);

  // Close on escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeMenu();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, closeMenu]);

  return { isOpen, setIsOpen, menuRef, closeMenu };
};

const MenuToggleButton = React.memo(
  ({
    user,
    isOpen,
    setIsOpen,
    headerOnLight,
    rawDisplayName,
    avatarInitial,
    hasProfileImage,
    navigate,
    t,
    showNotification,
  }: {
    user: MobileExpandableMenuUser | null;
    isOpen: boolean;
    setIsOpen: (open: boolean) => void;
    headerOnLight: boolean;
    rawDisplayName: string;
    avatarInitial: string;
    hasProfileImage: boolean;
    navigate: (path: string) => void;
    t: (
      namespace: string,
      key: string,
      fallback?: string,
      variables?: Record<string, unknown>
    ) => string;
    showNotification: boolean;
  }) => (
    <div className={POSITION_SCALE.relative}>
      <button
        onClick={() => {
          if (user) {
            setIsOpen(false);
            navigate(createPageUrl('UserDashboard'));
            return;
          }
          setIsOpen(!isOpen);
        }}
        className={`flex items-center justify-center ${SIZE_SCALE.w8} ${SIZE_SCALE['8']} sm:${SIZE_SCALE.w9} sm:${SIZE_SCALE['9']} rounded-full ${INTERACTIVE_STATES.transitions.all} active:scale-95 ${headerOnLight ? `${COLOR_SCALE.foreground} hover:${COLOR_SCALE['surface-subtle']} hover:${COLOR_SCALE.foreground}` : `${COLOR_SCALE['header-dark']}`} ${isOpen ? `${COLOR_SCALE['surface-subtle']} ${COLOR_SCALE.foreground}` : ''}`}
        aria-label={
          user
            ? t('aria', 'go_to_dashboard', 'Go to dashboard')
            : t('aria', 'open_menu', 'Open menu')
        }
        aria-expanded={user ? false : isOpen}
      >
        <UserAvatar
          user={user}
          rawDisplayName={rawDisplayName}
          avatarInitial={avatarInitial}
          hasProfileImage={hasProfileImage}
        />
      </button>

      {/* Pulse indicator for notification */}
      {showNotification && (
        <span className={`absolute -top-1 -right-1 flex ${SIZE_SCALE['3']} ${SIZE_SCALE.w3}`}>
          <span
            className={`animate-ping absolute inline-flex h-full w-full ${RADIUS_SCALE.full} ${COLOR_SCALE['gray-900']} opacity-75`}
          ></span>
          <span
            className={`relative inline-flex ${RADIUS_SCALE.full} ${SIZE_SCALE['3']} ${SIZE_SCALE.w3} ${COLOR_SCALE['gray-900']}`}
          ></span>
        </span>
      )}
    </div>
  )
);

MenuToggleButton.displayName = 'MenuToggleButton';

const MenuContent = React.memo(
  ({
    user,
    uppercaseDisplayName,
    closeMenu,
    setShowSearch,
    currentLanguage,
    changeLanguage: _changeLanguage,
    selectedCurrency,
    handleLogout,
    handleSignup,
    handleLogin,
    pendingReportCount,
    t,
  }: {
    user: MobileExpandableMenuUser | null;
    uppercaseDisplayName: string;
    closeMenu: () => void;
    setShowSearch: (show: boolean) => void;
    currentLanguage: string;
    changeLanguage: (language: string) => void;
    selectedCurrency: string;
    handleLogout: () => void;
    handleSignup: () => void;
    handleLogin: () => void;
    pendingReportCount: number;
    t: (
      namespace: string,
      key: string,
      fallback?: string,
      variables?: Record<string, unknown>
    ) => string;
  }) => (
    <div
      className={`absolute top-full right-0 ${MARGIN_TOP['2']} ${SIZE_SCALE.w80} ${COLOR_SCALE.surface} ${RADIUS_SCALE.xl} ${SHADOW_SCALE['2xl']} border ${COLOR_SCALE['admin-border']} z-[99999] overflow-hidden`}
      role="dialog"
      aria-label={t('aria', 'mobile_actions_menu', 'Mobile actions menu')}
    >
      {/* Header */}
      <div
        className={`flex items-center justify-between ${SURFACE_PADDING['4']} border-b ${COLOR_SCALE['admin-border']} bg-gradient-to-r from-gray-50 to-white`}
      >
        <h3 className={`text-sm ${FONT_WEIGHTS.semibold} ${COLOR_SCALE.foreground}`}>
          {user ? uppercaseDisplayName : t('nav', 'menu', 'Menu')}
        </h3>
        <button
          onClick={closeMenu}
          className={`${COLOR_SCALE['admin-muted-text']} hover:${COLOR_SCALE['gray-700']} transition-colors ${SURFACE_PADDING['1']} hover:${COLOR_SCALE['surface-subtle']} ${RADIUS_SCALE.lg}`}
          aria-label={t('aria', 'close_menu', 'Close menu')}
        >
          <X className={`${SIZE_SCALE.w4} ${SIZE_SCALE.h4}`} />
        </button>
      </div>

      <div
        className={`${RESPONSIVE_SIZE_SCALE.maxH.sm['70vh']} ${RESPONSIVE_SIZE_SCALE.maxH.md['70vh']} ${OVERFLOW_Y.auto}`}
      >
        {/* Search Section */}
        <div className={`${SURFACE_PADDING['3']} border-b ${COLOR_SCALE['admin-border']}`}>
          <button
            onClick={() => {
              setShowSearch(true);
              closeMenu();
            }}
            className={`flex items-center ${SPACE_X_SCALE['3']} w-full ${PADDING_X['4']} ${PADDING_Y['3']} ${RADIUS_SCALE.lg} bg-gradient-to-r from-gray-50 to-gray-100/50 hover:from-gray-100 hover:to-gray-200/50 ${COLOR_SCALE['gray-700']} ${INTERACTIVE_STATES.transitions.all}`}
          >
            <Search className={`${SIZE_SCALE.w5} ${SIZE_SCALE['5']} flex-shrink-0`} />
            <span className={`${FONT_WEIGHTS.medium} text-sm`}>{t('nav', 'search', 'Search')}</span>
          </button>
        </div>

        {/* User Section */}
        {getUserSection(
          user,
          closeMenu,
          handleLogout,
          handleLogin,
          handleSignup,
          pendingReportCount,
          t
        )}

        {/* Language & Currency Section */}
        <div className={`${SURFACE_PADDING['3']} border-b ${COLOR_SCALE['admin-border']}`}>
          <div className={`${SPACE_Y_SCALE['2']}`}>
            <div className={`flex items-center justify-between`}>
              <span className={`text-sm ${FONT_WEIGHTS.medium} ${COLOR_SCALE.foreground}`}>
                {t('nav', 'language', 'Language')}
              </span>
              <LanguageSelector currentLanguage={currentLanguage} variant="minimal" />
            </div>
            <div className={`flex items-center justify-between`}>
              <span className={`text-sm ${FONT_WEIGHTS.medium} ${COLOR_SCALE.foreground}`}>
                {t('nav', 'currency', 'Currency')}
              </span>
              <CurrencySelector selectedCurrency={selectedCurrency} variant="minimal" />
            </div>
          </div>
        </div>

        {/* Navigation Links */}
        {getNavigationLinks(closeMenu, t)}
      </div>
    </div>
  )
);

MenuContent.displayName = 'MenuContent';

// Extracted component for the expandable menu panel to reduce complexity

const MobileMenuPanel = React.memo(
  ({
    user,
    uppercaseDisplayName,
    closeMenu,
    setShowSearch,
    currentLanguage,
    changeLanguage,
    selectedCurrency,
    handleLogout,
    handleSignup,
    handleLogin,
    pendingReportCount,
    showNotification,
    dismissNotification,
    t,
  }: {
    user: MobileExpandableMenuUser | null;
    uppercaseDisplayName: string;
    closeMenu: () => void;
    setShowSearch: (show: boolean) => void;
    currentLanguage: string;
    changeLanguage: (language: string) => void;
    selectedCurrency: string;
    handleLogout: () => void;
    handleSignup: () => void;
    handleLogin: () => void;
    pendingReportCount: number;
    showNotification: boolean;
    dismissNotification: () => void;
    t: (
      namespace: string,
      key: string,
      fallback?: string,
      variables?: Record<string, unknown>
    ) => string;
  }) => (
    <div
      className={`absolute top-full right-0 ${MARGIN_TOP['2']} ${SIZE_SCALE.w80} ${COLOR_SCALE.surface} ${RADIUS_SCALE.xl} ${SHADOW_SCALE['2xl']} border ${COLOR_SCALE['admin-border']} z-[99999] overflow-hidden`}
      role="dialog"
      aria-label={t('aria', 'mobile_actions_menu', 'Mobile actions menu')}
    >
      {/* Header */}
      <div
        className={`flex items-center justify-between ${SURFACE_PADDING['4']} border-b ${COLOR_SCALE['admin-border']} bg-gradient-to-r from-gray-50 to-white`}
      >
        <h3 className={`text-sm ${FONT_WEIGHTS.semibold} ${COLOR_SCALE.foreground}`}>
          {user ? uppercaseDisplayName : t('nav', 'menu', 'Menu')}
        </h3>
        <button
          onClick={closeMenu}
          className={`${COLOR_SCALE['admin-muted-text']} hover:${COLOR_SCALE['gray-700']} transition-colors ${SURFACE_PADDING['1']} hover:${COLOR_SCALE['surface-subtle']} ${RADIUS_SCALE.lg}`}
          aria-label={t('aria', 'close_menu', 'Close menu')}
        >
          <X className={`${SIZE_SCALE.w4} ${SIZE_SCALE.h4}`} />
        </button>
      </div>

      <div
        className={`${RESPONSIVE_SIZE_SCALE.maxH.sm['70vh']} ${RESPONSIVE_SIZE_SCALE.maxH.md['70vh']} ${OVERFLOW_Y.auto}`}
      >
        {/* Search Section */}
        <div className={`${SURFACE_PADDING['3']} border-b ${COLOR_SCALE['admin-border']}`}>
          <button
            onClick={() => {
              setShowSearch(true);
              closeMenu();
            }}
            className={`flex items-center ${SPACE_X_SCALE['3']} w-full ${PADDING_X['4']} ${PADDING_Y['3']} ${RADIUS_SCALE.lg} bg-gradient-to-r from-gray-50 to-gray-100/50 hover:from-gray-100 hover:to-gray-200/50 ${COLOR_SCALE['gray-700']} ${INTERACTIVE_STATES.transitions.all}`}
          >
            <Search className={`${SIZE_SCALE.w5} ${SIZE_SCALE['5']} flex-shrink-0`} />
            <span className={`${FONT_WEIGHTS.medium} text-sm`}>{t('nav', 'search', 'Search')}</span>
          </button>
        </div>

        {/* User Section */}
        {user ? (
          <div className={`${SURFACE_PADDING['3']} border-b ${COLOR_SCALE['admin-border']}`}>
            <div className={`${SPACE_Y_SCALE['1']}`}>
              <Link
                to={createPageUrl('UserDashboard')}
                onClick={closeMenu}
                className={`flex items-center ${SPACE_X_SCALE['3']} ${PADDING_X['4']} ${PADDING_Y['3']} hover:${COLOR_SCALE['surface-subtle']} ${RADIUS_SCALE.lg} ${COLOR_SCALE['gray-700']} hover:${COLOR_SCALE.foreground} transition-colors`}
              >
                <User
                  className={`${SIZE_SCALE.w5} ${SIZE_SCALE['5']} flex-shrink-0 ${COLOR_SCALE['foreground-muted']}`}
                />
                <div className={`${FLEX.flex1} ${SIZE_SCALE.minW0}`}>
                  <div className={`${FONT_WEIGHTS.medium} text-sm truncate uppercase`}>
                    {uppercaseDisplayName}
                  </div>
                  <div className={`${TYPE_SCALE.xs} ${COLOR_SCALE['admin-muted-text']} truncate`}>
                    {user.email}
                  </div>
                </div>
              </Link>

              {((user.is_admin ?? false) || (user.partner_permissions ?? false)) && (
                <Link
                  to={createPageUrl(user.is_admin ? 'AdminDashboard' : 'PartnerDashboard')}
                  onClick={closeMenu}
                  className={`flex items-center ${SPACE_X_SCALE['3']} ${PADDING_X['4']} ${PADDING_Y['3']} hover:${COLOR_SCALE['surface-subtle']} ${RADIUS_SCALE.lg} ${COLOR_SCALE['gray-700']} hover:${COLOR_SCALE.foreground} transition-colors`}
                >
                  <Settings
                    className={`${SIZE_SCALE.w5} ${SIZE_SCALE['5']} flex-shrink-0 ${COLOR_SCALE['foreground-muted']}`}
                  />
                  <span className={`${FONT_WEIGHTS.medium} text-sm`}>
                    {user.is_admin
                      ? t('navigation', 'admin', 'Admin')
                      : t('navigation', 'partners', 'Partners')}
                  </span>
                  {pendingReportCount > 0 && (
                    <span
                      className={`${MARGIN_AUTO} ${COLOR_SCALE['red-600-bg']} text-white text-xs font-bold ${RADIUS_SCALE.full} ${PADDING_X['2']} ${PADDING_Y['0.5']}`}
                    >
                      {pendingReportCount}
                    </span>
                  )}
                </Link>
              )}

              <button
                onClick={() => {
                  handleLogout();
                  closeMenu();
                }}
                className={`flex items-center ${SPACE_X_SCALE['3']} ${PADDING_X['4']} ${PADDING_Y['3']} ${COLOR_SCALE['red-50-bg']} ${RADIUS_SCALE.lg} ${COLOR_SCALE['red-600']} ${COLOR_SCALE['red-700']} transition-colors w-full text-left`}
              >
                <LogOut className={`${SIZE_SCALE.iconMd} flex-shrink-0`} />
                <span className={`${FONT_WEIGHTS.medium} text-sm`}>
                  {t('navigation', 'logout', 'Log Out')}
                </span>
              </button>
            </div>
          </div>
        ) : (
          <div
            className={`${SURFACE_PADDING['3']} ${SPACE_Y_SCALE['2']} border-b ${COLOR_SCALE['admin-border']}`}
          >
            <Button
              onClick={() => {
                handleSignup();
                closeMenu();
              }}
              variant="outline"
              className={`w-full text-sm ${FONT_WEIGHTS.medium} ${COLOR_SCALE['gray-900']} ${COLOR_SCALE.foreground} hover:${COLOR_SCALE['surface-muted']} ${RADIUS_SCALE.lg}`}
            >
              {t('nav', 'sign_up', 'Sign Up')}
            </Button>
            <Button
              onClick={() => {
                handleLogin();
                closeMenu();
              }}
              className={`w-full text-sm ${FONT_WEIGHTS.medium} ${COLOR_SCALE['gray-900']} ${COLOR_SCALE['gray-900']} text-white ${RADIUS_SCALE.lg}`}
            >
              {t('nav', 'log_in', 'Log In')}
            </Button>
          </div>
        )}

        {/* Language Selection */}
        <div className={`${SURFACE_PADDING['3']}`}>
          <div
            className={`${PADDING_X['4']} ${PADDING_Y['2']} text-xs font-semibold ${COLOR_SCALE['admin-muted-text']} uppercase tracking-wider flex items-center ${SPACE_X_SCALE['2']}`}
          >
            <Globe className={`${SIZE_SCALE.w4} ${SIZE_SCALE.h4}`} />
            <span>{t('nav', 'language', 'Language')}</span>
            {showNotification && (
              <span className={`flex ${SIZE_SCALE['2']} ${SIZE_SCALE.w2}`}>
                <span
                  className={`animate-ping absolute inline-flex ${SIZE_SCALE['2']} ${SIZE_SCALE.w2} ${RADIUS_SCALE.full} ${COLOR_SCALE['gray-900']} opacity-75`}
                ></span>
                <span
                  className={`relative inline-flex ${RADIUS_SCALE.full} ${SIZE_SCALE['2']} ${SIZE_SCALE.w2} ${COLOR_SCALE['gray-900']}`}
                ></span>
              </span>
            )}
          </div>

          {/* Language notification banner for mobile */}
          {showNotification && (
            <div
              className={`${MARGIN_X['2']} ${MARGIN_TOP['2']} ${MARGIN_BOTTOM['3']} ${SURFACE_PADDING['3']} ${COLOR_SCALE['surface-muted']} border ${COLOR_SCALE['admin-border']} ${RADIUS_SCALE.lg}`}
            >
              <div className={`flex items-start ${SPACE_X_SCALE['2']} ${MARGIN_BOTTOM['2']}`}>
                <div
                  className={`${SIZE_SCALE.w6} ${SIZE_SCALE['6']} ${RADIUS_SCALE.full} ${COLOR_SCALE['gray-900']} flex items-center justify-center flex-shrink-0 ${MARGIN_TOP['0.5']}`}
                >
                  <Globe className={`${SIZE_SCALE.w35} ${SIZE_SCALE.h35} text-white`} />
                </div>
                <div className={FLEX.flex1}>
                  <p className={`${TYPE_SCALE.xs} ${COLOR_SCALE['gray-700']} leading-relaxed`}>
                    {t('notification', 'language_message', "You're viewing this site in")}{' '}
                    <span className={`${FONT_WEIGHTS.semibold} ${COLOR_SCALE.foreground}`}>
                      {languages.find(l => l.code === currentLanguage)?.name ??
                        currentLanguage.toUpperCase()}
                    </span>
                    .{' '}
                    {t(
                      'notification',
                      'language_question',
                      'Would you like to continue in this language?'
                    )}
                  </p>
                </div>
              </div>
              <div className={`flex ${SPACE_X_SCALE['2']}`}>
                <button
                  onClick={dismissNotification}
                  className={`${FLEX.flex1} ${FLEX.flex} ${FLEX_ALIGN.center} ${FLEX_JUSTIFY.center} ${GAP_SCALE['1.5']} ${PADDING_X['3']} ${PADDING_Y['2']} ${COLOR_SCALE['gray-900']} text-white ${RADIUS_SCALE.lg} ${INTERACTIVE_STATES.transitions.all} text-xs font-medium`}
                >
                  <Check className={`${SIZE_SCALE.iconXs}`} />
                  <span>{t('notification', 'keep_language', 'Yes, continue')}</span>
                </button>
              </div>
            </div>
          )}

          <div className={`${SPACE_Y_SCALE['1']} ${MARGIN_TOP['2']}`}>
            {languages.map(lang => (
              <button
                key={lang.code}
                onClick={() => {
                  changeLanguage(lang.code);
                  dismissNotification();
                }}
                className={`flex items-center justify-between ${PADDING_X['4']} ${PADDING_Y['3']} hover:${COLOR_SCALE['surface-subtle']} ${RADIUS_SCALE.lg} w-full text-left transition-colors ${currentLanguage === lang.code ? `${COLOR_SCALE['surface-subtle']} ${COLOR_SCALE['gray-800']}` : `${COLOR_SCALE['gray-700']}`}`}
              >
                <div
                  className={`${FLEX.flex} ${FLEX_ALIGN.center} ${SPACE_X_SCALE['3']} ${SIZE_SCALE.minW0} ${FLEX.flex1}`}
                >
                  <span className={`${TYPE_SCALE.lg} flex-shrink-0`}>{lang.flag}</span>
                  <div className={`${SIZE_SCALE.minW0} ${FLEX.flex1}`}>
                    <div className={`${FONT_WEIGHTS.medium} text-sm truncate`}>{lang.name}</div>
                    <div className={`${TYPE_SCALE.xs} ${COLOR_SCALE['admin-muted-text']} truncate`}>
                      {lang.nativeName}
                    </div>
                  </div>
                </div>
                {currentLanguage === lang.code && (
                  <CheckCircle
                    className={`${SIZE_SCALE.w4} ${SIZE_SCALE.h4} ${COLOR_SCALE['status-success']} flex-shrink-0 ${MARGIN_LEFT['2']}`}
                  />
                )}
              </button>
            ))}
          </div>

          {/* Currency Selection */}
          <div
            className={`${MARGIN_TOP['6']} ${PADDING_TOP['4']} border-t ${COLOR_SCALE['admin-border']}`}
          >
            <div
              className={`${PADDING_X['4']} ${PADDING_BOTTOM['3']} text-xs font-semibold ${COLOR_SCALE['admin-muted-text']} uppercase tracking-wider flex items-center ${SPACE_X_SCALE['2']}`}
            >
              <CircleDollarSign className={`${SIZE_SCALE.w4} ${SIZE_SCALE.h4}`} />
              <span>{t('nav', 'currency', 'Price (in country)')}</span>
            </div>
            <div className={`${PADDING_X['2']} ${SPACE_Y_SCALE['2']}`}>
              <CurrencySwitcher
                hideLabel
                size="sm"
                variant="ghost"
                ariaLabel={t('currency', 'change_currency', 'Change currency')}
                className={`w-full justify-center rounded-full ${PADDING_X['4']} ${PADDING_Y['2']} text-sm font-semibold uppercase tracking-wide ${COLOR_SCALE['gray-700']} hover:${COLOR_SCALE.foreground} hover:${COLOR_SCALE['surface-subtle']} focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-0`}
              />
              <p
                className={`${TYPE_SCALE['2xs']} leading-relaxed ${COLOR_SCALE['admin-muted-text']} ${PADDING_X['2']} text-center`}
              >
                {t(
                  'currency',
                  'mobile_hint',
                  'Convert treatment prices instantly to {{currency}}.',
                  {
                    currency: selectedCurrency,
                  }
                )}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
);

MobileMenuPanel.displayName = 'MobileMenuPanel';

const getUserSection = (
  user: MobileExpandableMenuUser | null,
  closeMenu: () => void,
  handleLogout: () => void,
  handleLogin: () => void,
  handleSignup: () => void,
  pendingReportCount: number,
  t: (
    namespace: string,
    key: string,
    fallback?: string,
    variables?: Record<string, unknown>
  ) => string
) => {
  if (user) {
    return (
      <div className={`${SURFACE_PADDING['3']} border-b ${COLOR_SCALE['admin-border']}`}>
        <div className={`${SPACE_Y_SCALE['1']}`}>
          <Link
            to={createPageUrl('UserDashboard')}
            onClick={closeMenu}
            className={`flex items-center ${SPACE_X_SCALE['3']} w-full ${PADDING_X['4']} ${PADDING_Y['3']} ${RADIUS_SCALE.lg} hover:${COLOR_SCALE['surface-subtle']} ${INTERACTIVE_STATES.transitions.all}`}
          >
            <User className={`${SIZE_SCALE.w5} ${SIZE_SCALE['5']} flex-shrink-0`} />
            <span className={`${FONT_WEIGHTS.medium} text-sm`}>
              {t('nav', 'account', 'Account')}
            </span>
            {pendingReportCount > 0 && (
              <Badge
                variant="destructive"
                className={`ml-auto ${TYPE_SCALE.xs} ${SIZE_SCALE.w5} ${SIZE_SCALE.h5} flex items-center justify-center`}
              >
                {pendingReportCount}
              </Badge>
            )}
          </Link>
          <button
            onClick={() => {
              handleLogout();
              closeMenu();
            }}
            className={`flex items-center ${SPACE_X_SCALE['3']} w-full ${PADDING_X['4']} ${PADDING_Y['3']} ${RADIUS_SCALE.lg} hover:${COLOR_SCALE['surface-subtle']} ${INTERACTIVE_STATES.transitions.all} text-red-600`}
          >
            <LogOut className={`${SIZE_SCALE.w5} ${SIZE_SCALE['5']} flex-shrink-0`} />
            <span className={`${FONT_WEIGHTS.medium} text-sm`}>{t('nav', 'logout', 'Logout')}</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`${SURFACE_PADDING['3']} border-b ${COLOR_SCALE['admin-border']}`}>
      <div className={`${SPACE_Y_SCALE['1']}`}>
        <button
          onClick={() => {
            handleLogin();
            closeMenu();
          }}
          className={`flex items-center ${SPACE_X_SCALE['3']} w-full ${PADDING_X['4']} ${PADDING_Y['3']} ${RADIUS_SCALE.lg} hover:${COLOR_SCALE['surface-subtle']} ${INTERACTIVE_STATES.transitions.all}`}
        >
          <LogIn className={`${SIZE_SCALE.w5} ${SIZE_SCALE['5']} flex-shrink-0`} />
          <span className={`${FONT_WEIGHTS.medium} text-sm`}>{t('nav', 'login', 'Login')}</span>
        </button>
        <button
          onClick={() => {
            handleSignup();
            closeMenu();
          }}
          className={`flex items-center ${SPACE_X_SCALE['3']} w-full ${PADDING_X['4']} ${PADDING_Y['3']} ${RADIUS_SCALE.lg} hover:${COLOR_SCALE['surface-subtle']} ${INTERACTIVE_STATES.transitions.all}`}
        >
          <UserPlus className={`${SIZE_SCALE.w5} ${SIZE_SCALE['5']} flex-shrink-0`} />
          <span className={`${FONT_WEIGHTS.medium} text-sm`}>{t('nav', 'signup', 'Sign Up')}</span>
        </button>
      </div>
    </div>
  );
};

const getNavigationLinks = (
  closeMenu: () => void,
  t: (
    namespace: string,
    key: string,
    fallback?: string,
    variables?: Record<string, unknown>
  ) => string
) => (
  <div className={`${SURFACE_PADDING['3']}`}>
    <div className={`${SPACE_Y_SCALE['1']}`}>
      <Link
        to={createPageUrl('Home')}
        onClick={closeMenu}
        className={`flex items-center ${SPACE_X_SCALE['3']} w-full ${PADDING_X['4']} ${PADDING_Y['3']} ${RADIUS_SCALE.lg} hover:${COLOR_SCALE['surface-subtle']} ${INTERACTIVE_STATES.transitions.all}`}
      >
        <Home className={`${SIZE_SCALE.w5} ${SIZE_SCALE['5']} flex-shrink-0`} />
        <span className={`${FONT_WEIGHTS.medium} text-sm`}>{t('nav', 'home', 'Home')}</span>
      </Link>
      <Link
        to={createPageUrl('Treatments')}
        onClick={closeMenu}
        className={`flex items-center ${SPACE_X_SCALE['3']} w-full ${PADDING_X['4']} ${PADDING_Y['3']} ${RADIUS_SCALE.lg} hover:${COLOR_SCALE['surface-subtle']} ${INTERACTIVE_STATES.transitions.all}`}
      >
        <Stethoscope className={`${SIZE_SCALE.w5} ${SIZE_SCALE['5']} flex-shrink-0`} />
        <span className={`${FONT_WEIGHTS.medium} text-sm`}>
          {t('nav', 'treatments', 'Treatments')}
        </span>
      </Link>
      <Link
        to={createPageUrl('Providers')}
        onClick={closeMenu}
        className={`flex items-center ${SPACE_X_SCALE['3']} w-full ${PADDING_X['4']} ${PADDING_Y['3']} ${RADIUS_SCALE.lg} hover:${COLOR_SCALE['surface-subtle']} ${INTERACTIVE_STATES.transitions.all}`}
      >
        <Building className={`${SIZE_SCALE.w5} ${SIZE_SCALE['5']} flex-shrink-0`} />
        <span className={`${FONT_WEIGHTS.medium} text-sm`}>
          {t('nav', 'providers', 'Providers')}
        </span>
      </Link>
      <Link
        to={createPageUrl('Blog')}
        onClick={closeMenu}
        className={`flex items-center ${SPACE_X_SCALE['3']} w-full ${PADDING_X['4']} ${PADDING_Y['3']} ${RADIUS_SCALE.lg} hover:${COLOR_SCALE['surface-subtle']} ${INTERACTIVE_STATES.transitions.all}`}
      >
        <FileText className={`${SIZE_SCALE.w5} ${SIZE_SCALE['5']} flex-shrink-0`} />
        <span className={`${FONT_WEIGHTS.medium} text-sm`}>{t('nav', 'blog', 'Blog')}</span>
      </Link>
    </div>
  </div>
);

const MobileExpandableMenu = React.memo(
  ({
    user,
    currentLanguage,
    changeLanguage,
    setShowSearch,
    handleLogout,
    handleSignup,
    handleLogin,
    headerOnLight,
    pendingReportCount,
    t,
  }: MobileExpandableMenuProps) => {
    const { isOpen, setIsOpen, menuRef, closeMenu } = useMobileMenu();
    const defaultAccountLabel = t('navigation', 'account', 'Account');
    const { rawDisplayName, uppercaseDisplayName, avatarInitial, hasProfileImage } =
      getUserDisplayInfo(user, defaultAccountLabel);
    const { selectedCurrency } = useCurrency();
    const navigate = useNavigate();

    // Wrap navigate to ensure void return type
    const safeNavigate = (path: string): void => {
      void navigate(path);
    };

    // Language notification on first visit
    const { showNotification, dismissNotification: _dismissNotification } =
      useLanguageNotification();

    return (
      <div className={`${RESPONSIVE_DISPLAY.hidden.lg} ${POSITION_SCALE.relative}`} ref={menuRef}>
        {/* Toggle Button */}
        <MenuToggleButton
          user={user}
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          headerOnLight={headerOnLight}
          rawDisplayName={rawDisplayName}
          avatarInitial={avatarInitial}
          hasProfileImage={hasProfileImage}
          navigate={safeNavigate}
          t={t}
          showNotification={showNotification}
        />

        {/* Expandable Panel */}
        {isOpen && (
          <MenuContent
            user={user}
            uppercaseDisplayName={uppercaseDisplayName}
            closeMenu={closeMenu}
            setShowSearch={setShowSearch}
            currentLanguage={currentLanguage}
            changeLanguage={changeLanguage}
            selectedCurrency={selectedCurrency}
            handleLogout={handleLogout}
            handleSignup={handleSignup}
            handleLogin={handleLogin}
            pendingReportCount={pendingReportCount}
            t={t}
          />
        )}
      </div>
    );
  }
);

const MobileExpandableMenuWithMonitor = withMemoryLeakMonitor(MobileExpandableMenu, {
  componentName: 'MobileExpandableMenu',
  enableRenderTracking: true,
  enableLeakDetection: true,
  warnOnExcessiveRenders: true,
});

MobileExpandableMenu.displayName = 'MobileExpandableMenu';

export { MobileExpandableMenuWithMonitor as MobileExpandableMenu };
